import { ApiError, apiFetch } from '@/lib/apiFetch';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

type Produkt = {
  code: string;
  name: string;
  image: string;
  kategorie: string;
  qty: Record<string, number>;
  m: Record<string, number>;
  qty_total: number;
  m_total: number;
};

type Latka = {
  id: string;
  code: string;
  name: string;
  type: string;
  image: string;
  stock_m: number;
  incoming: { m: number; expected_at: number | null }[];
  m: Record<string, number>;
  m_total: number;
  products: Produkt[];
};

type Chybi = {
  code: string;
  name: string;
  kategorie: string;
  reason: 'latka' | 'spotreba';
  variants: string[];
  qty_total: number;
};

type Data = {
  terms: string[];
  latky: Latka[];
  missing: Chybi[];
  /** Barvy druhů ze Skladu látek (druh → hex). */
  type_colors: Record<string, string>;
};

const BEZ_DRUHU = 'Bez druhu';

const DNY = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];

/** „2026-10-20" → „Út 20. 10.". */
function fmtTerm(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return `${DNY[d.getDay()] ?? ''} ${d.getDate()}. ${d.getMonth() + 1}.`;
}

/** Unix ms → „20. 10.". */
function fmtDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getDate()}. ${d.getMonth() + 1}.`;
}

/** Metry s jedním desetinným místem, prázdné = pomlčka. */
function fmtM(m: number | undefined): string {
  if (!m) return '—';
  return `${String(Math.round(m * 10) / 10).replace('.', ',')} m`;
}

/**
 * Kontrola látek — kolik metrů které hlavní látky spotřebuje to, co zbývá
 * nastříhat (stejné kusy jako Plán stříhání), proti stavu Skladu látek.
 * Termín, od kterého látka nevystačí, je zvýrazněný; objednané role se
 * do skladu nepočítají, jen se vypíšou s datem dodání.
 */
export function KontrolaLatekPage() {
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [doTerminu, setDoTerminu] = useState('');
  const [jenChybi, setJenChybi] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [druh, setDruh] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await apiFetch<Data>('/api/vyroba/striharna/latky'));
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Nepodařilo se načíst potřebu látek.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Omezení „do termínu" — termíny po něm se skryjí a nepočítají do Celkem.
  const terms = useMemo(
    () => (data?.terms ?? []).filter((t) => !doTerminu || t <= doTerminu),
    [data, doTerminu],
  );
  const all = useMemo(() => {
    const out = (data?.latky ?? []).map((l) => {
      // První termín, ve kterém kumulovaná potřeba přeleze sklad.
      let cum = 0;
      let shortFrom: string | null = null;
      for (const t of terms) {
        cum += l.m[t] ?? 0;
        if (!shortFrom && cum > l.stock_m + 0.005) shortFrom = t;
      }
      const missing = Math.max(0, cum - l.stock_m);
      const incoming = l.incoming.reduce((s, i) => s + i.m, 0);
      return { ...l, total: cum, missing, shortFrom, incomingM: incoming };
    });
    return out
      .filter((l) => l.total > 0)
      .sort((a, b) => b.missing - a.missing || b.total - a.total);
  }, [data, terms]);
  // Dlaždice druhů jako ve Skladu látek — kolik látek a kolik jich chybí.
  const druhy = useMemo(() => {
    const map = new Map<string, { type: string; count: number; short: number; total: number }>();
    for (const l of all) {
      const key = l.type || BEZ_DRUHU;
      const d = map.get(key) ?? { type: key, count: 0, short: 0, total: 0 };
      d.count++;
      if (l.missing > 0.005) d.short++;
      d.total += l.total;
      map.set(key, d);
    }
    return [...map.values()].sort((a, b) => a.type.localeCompare(b.type, 'cs'));
  }, [all]);
  const rows = useMemo(
    () =>
      all.filter(
        (l) => (!druh || (l.type || BEZ_DRUHU) === druh) && (!jenChybi || l.missing > 0.005),
      ),
    [all, druh, jenChybi],
  );
  const chybiCount = rows.filter((l) => l.missing > 0.005).length;

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Kontrola látek</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            Kolik metrů hlavních látek potřebujeme na to, co zbývá nastříhat, a kolik jich máme ve
            Skladu látek. Spotřebu po velikostech nastavíš u kategorie v Databázi kategorií.
          </p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={jenChybi}
              onChange={(e) => setJenChybi(e.target.checked)}
              className="accent-primary"
            />
            Jen chybějící
          </label>
          <div className="flex items-center gap-2">
            <label className="text-sm text-text-secondary" htmlFor="do-terminu">
              Do termínu
            </label>
            <select
              id="do-terminu"
              value={doTerminu}
              onChange={(e) => setDoTerminu(e.target.value)}
              className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:border-primary"
            >
              <option value="">Všechny</option>
              {(data?.terms ?? []).map((t) => (
                <option key={t} value={t}>
                  {fmtTerm(t)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading && <p className="text-sm text-text-muted">Načítám…</p>}
      {error && !loading && (
        <div className="rounded-lg border border-danger/20 bg-danger-light p-4 mb-6 text-sm text-danger">
          {error}{' '}
          <button type="button" onClick={() => void load()} className="underline cursor-pointer">
            Zkusit znovu
          </button>
        </div>
      )}

      {data && !loading && chybiCount > 0 && !jenChybi && (
        <p className="mb-4 text-sm text-danger font-semibold">
          Na skladě nevystačí {chybiCount}{' '}
          {chybiCount === 1 ? 'látka' : chybiCount < 5 ? 'látky' : 'látek'}.
        </p>
      )}

      {data && !loading && data.missing.length > 0 && (
        <div className="rounded-lg border border-warning/40 bg-warning-light p-4 mb-6 text-sm text-text">
          <div className="font-semibold mb-1">
            Nezapočteno — {data.missing.length} produktů bez údajů
          </div>
          <ul className="space-y-0.5">
            {data.missing.map((m) => (
              <li key={m.code}>
                <span className="font-mono">{m.code}</span> {m.name}{' '}
                <span className="text-text-muted">
                  ({m.qty_total} ks) —{' '}
                  {m.reason === 'latka'
                    ? 'produkt nemá hlavní látku'
                    : `kategorie ${m.kategorie || '?'} nemá spotřebu látky${
                        m.variants.length ? ` pro velikost ${m.variants.join(', ')}` : ''
                      }`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {druhy.length > 1 && !loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-6">
          {druhy.map((d) => {
            const active = druh === d.type;
            return (
              <button
                key={d.type}
                type="button"
                onClick={() => setDruh(active ? '' : d.type)}
                aria-pressed={active}
                style={
                  data?.type_colors[d.type]
                    ? { backgroundColor: data.type_colors[d.type] }
                    : undefined
                }
                className={`text-left rounded-xl shadow-card bg-surface px-4 py-3 border-2 transition-all cursor-pointer hover:shadow-card-hover ${
                  active ? 'border-primary' : 'border-transparent'
                }`}
              >
                <div className="font-semibold text-text text-sm truncate">{d.type}</div>
                <div className="text-xs text-text-secondary mt-0.5">
                  {d.count} {d.count === 1 ? 'látka' : d.count < 5 ? 'látky' : 'látek'} ·{' '}
                  {fmtM(d.total)}
                </div>
                {d.short > 0 && (
                  <div className="text-xs font-semibold text-danger mt-0.5">chybí {d.short}</div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {data && !loading && rows.length === 0 && (
        <p className="text-sm text-text-muted">
          {jenChybi ? 'Všech látek je na skladě dost.' : 'Nic ke stříhání s vyplněnou látkou.'}
        </p>
      )}

      {rows.length > 0 && !loading && (
        <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-text-muted">
                <th className="px-4 py-3 min-w-[320px]">Látka</th>
                {terms.map((t) => (
                  <th key={t} className="px-3 py-3 text-right whitespace-nowrap">
                    {fmtTerm(t)}
                  </th>
                ))}
                <th className="px-3 py-3 text-right">Potřeba</th>
                <th className="px-3 py-3 text-right">Skladem</th>
                <th className="px-3 py-3 text-right">Chybí</th>
                <th className="px-4 py-3">Objednáno</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => {
                const isOpen = open === l.id;
                const products = l.products
                  .map((p) => ({
                    ...p,
                    sum: terms.reduce((s, t) => s + (p.qty[t] ?? 0), 0),
                    sumM: terms.reduce((s, t) => s + (p.m[t] ?? 0), 0),
                  }))
                  .filter((p) => p.sum > 0);
                const short = l.missing > 0.005;
                // Zelená = pokryje sklad, oranžová = pokryje až objednané na
                // cestě, červená = zbývá objednat.
                const stavBg = !short
                  ? 'bg-success-light'
                  : l.incomingM + 0.005 >= l.missing
                    ? 'bg-warning-light'
                    : 'bg-danger-light';
                return (
                  <Fragment key={l.id}>
                    <tr className="border-b border-border-light hover:bg-surface-hover">
                      <td className={`px-4 py-3 ${stavBg}`}>
                        <button
                          type="button"
                          onClick={() => setOpen(isOpen ? null : l.id)}
                          aria-expanded={isOpen}
                          className="flex items-center gap-2 cursor-pointer text-left"
                        >
                          {l.image ? (
                            <img
                              src={l.image}
                              alt=""
                              loading="lazy"
                              className="w-9 h-9 rounded-full object-cover border border-border shrink-0"
                            />
                          ) : null}
                          <span className="min-w-0">
                            <span className="block text-text font-medium whitespace-nowrap">
                              {l.code ? (
                                <span className="font-mono text-text-muted">{l.code} </span>
                              ) : null}
                              {l.name || '(bez názvu)'}
                            </span>
                            <span className="block text-xs text-text-muted whitespace-nowrap">
                              {l.type ? `${l.type} · ` : ''}
                              {isOpen ? '▾' : '▸'} {products.length} produktů
                            </span>
                          </span>
                        </button>
                      </td>
                      {terms.map((t) => (
                        <td
                          key={t}
                          className={`px-3 py-3 text-right tabular-nums ${
                            l.shortFrom && t >= l.shortFrom
                              ? 'text-danger font-semibold'
                              : 'text-text-secondary'
                          }`}
                        >
                          {fmtM(l.m[t])}
                        </td>
                      ))}
                      <td className="px-3 py-3 text-right tabular-nums font-bold text-text">
                        {fmtM(l.total)}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-text-secondary">
                        {fmtM(l.stock_m)}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        {short ? (
                          <>
                            <span className="font-bold text-danger">{fmtM(l.missing)}</span>
                            {l.shortFrom && (
                              <span className="block text-xs text-danger">
                                od {fmtTerm(l.shortFrom)}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-success">✓</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-text-secondary whitespace-nowrap">
                        {l.incoming.length === 0
                          ? '—'
                          : l.incoming.map((i) => (
                              <span key={`${i.expected_at ?? 'x'}-${i.m}`} className="block">
                                {fmtM(i.m)}
                                {i.expected_at ? ` · ${fmtDate(i.expected_at)}` : ''}
                              </span>
                            ))}
                        {short && l.incomingM > 0 && l.incomingM + 0.005 < l.missing && (
                          <span className="block text-danger">nestačí</span>
                        )}
                      </td>
                    </tr>
                    {isOpen &&
                      products.map((p) => (
                        <tr key={p.code} className="border-b border-border-light bg-bg">
                          <td className="px-4 py-2 pl-10">
                            <div className="flex items-center gap-2">
                              {p.image ? (
                                <img
                                  src={p.image}
                                  alt=""
                                  loading="lazy"
                                  className="w-8 h-8 rounded object-contain border border-border bg-surface shrink-0"
                                />
                              ) : null}
                              <div className="min-w-0">
                                <div className="text-text truncate">
                                  <span className="font-mono">{p.code}</span> {p.name}
                                </div>
                                <div className="text-xs text-text-muted">{p.kategorie}</div>
                              </div>
                            </div>
                          </td>
                          {terms.map((t) => (
                            <td
                              key={t}
                              className="px-3 py-2 text-right tabular-nums text-text-muted"
                            >
                              {p.qty[t] ? `${p.qty[t]} ks` : '—'}
                            </td>
                          ))}
                          <td className="px-3 py-2 text-right tabular-nums text-text-secondary whitespace-nowrap">
                            {p.sum} ks · {fmtM(p.sumM)}
                          </td>
                          <td colSpan={3} />
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
